import { Loader, Button as MantineButton, Tooltip } from '@mantine/core';
import { useSyncDetailDrawer } from '../hooks/useSyncDetailDrawer';
import {
  type RequestQuery,
  type ResponseData,
  useApiQuery,
  useTeam,
  useTeamPermission,
} from '@vrplatform/data';

type Props = {
  type: 'reservations' | 'deposits' | 'payments';
};

type SyncsApiQuery = RequestQuery<'get:/syncs'>;
type Sync = ResponseData<'get:/syncs'>['data'][number];

export function useRunningSyncSubscription(type: Props['type']) {
  const [{ id: teamId }] = useTeam();
  const canReadIntegrations = useTeamPermission('integrations:read');

  return useApiQuery(
    async (api, signal, args) => {
      const categories =
        args.type === 'reservations'
          ? ['propertyManagementSystem']
          : ['paymentGateway', 'bookingChannel'];
      const statuses = ['started', 'queued'] as const;

      const results = await Promise.all(
        categories.flatMap((category) =>
          statuses.map((status) =>
            api.GET('/syncs', {
              params: {
                query: {
                  category,
                  limit: 1,
                  sort: '-created',
                  status,
                } satisfies SyncsApiQuery,
              },
              signal,
            })
          )
        )
      );

      const syncs: Sync[] = [];
      for (const result of results) {
        if (result.error) throw result.error;
        syncs.push(...result.data.data);
      }

      const sync = syncs.sort(
        (left, right) =>
          Date.parse(right.createdAt) - Date.parse(left.createdAt)
      )[0];

      return sync
        ? {
            id: sync.id,
            status: sync.status,
            connection: {
              name: sync.connection.name || '-',
            },
          }
        : null;
    },
    {
      // Every UI flow that starts a sync invalidates the `connections` group,
      // so the indicator appears without waiting for the idle poll.
      queryGroup: 'connections',
      uniqueQueryKey: 'running-sync-indicator',
      queryKeys: ['tasks', 'automations', 'jobPlans'],
      skip: !teamId || !canReadIntegrations,
      keepPreviousData: true,
      variables: {
        teamId,
        type,
      },
      // Poll quickly only while a sync is active; idle pages still pick up
      // syncs started elsewhere, such as scheduled automations.
      poll: {
        interval: (sync) => (sync ? 15_000 : 60_000),
        isCompleted: () => false,
      },
    }
  );
}

export const RunningSyncIndicator = ({ type }: Props) => {
  const { data } = useRunningSyncSubscription(type);
  const { open } = useSyncDetailDrawer();

  if (data) {
    return (
      <Tooltip
        withArrow
        label={
          data.connection.name
            ? `Syncing ${data.connection.name}...`
            : 'Syncing data...'
        }
      >
        <MantineButton
          variant="transparent"
          leftSection={<Loader size={14} color="gray" />}
          c="neutral"
          onClick={() => open(data.id, { type: 'push' })}
          size="xs"
          sx={{
            fontWeight: 400,
          }}
        >
          Syncing data...
        </MantineButton>
      </Tooltip>
    );
  }

  return null;
};
